import { API } from '@/api'
import { CreateTeamMemberRes } from '@/api/type-response'
import { DistributeCreditRes } from '@/api/type-response'
import { message } from '@/components/base/Notification'
import { errorMsg, ignoreErrorCodes } from '@/consts/errorMsg'
import { TeamRole } from '@/consts/team'
import { formatErrCode, handleRequestError, handleResponseError } from '@/utils/error'

import { normalizeMember } from '../adapters/teamMember'
import type { AddMemberForm } from '../types/types'

function assertOk(resp: any, errMap: Record<string, any>) {
  const raw = resp?.code ?? resp?.errCode ?? 0
  const code = Number(raw)
  const platformIgnoreCodes = ignoreErrorCodes['platform'] || []
  if (platformIgnoreCodes.includes(code)) return resp
  if (code !== 0) {
    const entry = errMap[String(code)] || errMap.default
    const msg = entry?.msg || resp?.msg || errMap?.default?.msg || 'Network error'
    const e = new Error(msg)
    Object.assign(e, { code, __business: true })
    throw e
  }
  return resp
}

const USERNAME_RE = /^[A-Za-z0-9_-]{1,16}$/

export async function fetchAllMembers(teamResID: string) {
  const PER_PAGE = 500
  const first: any = await API.getTeamMemberList({ res_id: teamResID, page: 1, per_page: PER_PAGE })
  let list = first?.members ?? []
  const pages = first?.pagination?.numPages ?? 1
  if (pages > 1) {
    const ids = Array.from({ length: pages - 1 }, (_, i) => i + 2)
    const CONCURRENCY = 4
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const slice = ids.slice(i, i + CONCURRENCY)
      const res = await Promise.allSettled(
        slice.map(
          (p) => API.getTeamMemberList({ res_id: teamResID, page: p, per_page: PER_PAGE }) as any,
        ),
      )
      for (const r of res) if (r.status === 'fulfilled') list = list.concat(r.value?.members ?? [])
    }
  }
  return list
}

function validateUsername(name: string) {
  if (!USERNAME_RE.test(name))
    throw new Error('Username must be 1-16 characters: A-Z, a-z, 0-9, _ or -')
}

export async function createMemberFlow(teamResID: string, teamDomain: string, form: AddMemberForm) {
  if (!teamResID) throw new Error('Missing team resID')
  if (!teamDomain) throw new Error('Missing team domain')

  const errMap = formatErrCode({
    default: { msg: 'Failed to create member' },
    196: errorMsg[196],
    202: errorMsg[202],
    203: errorMsg[203],
    195: errorMsg[195],
    164: errorMsg[164],
    197: errorMsg[197],
    198: errorMsg[198],
  })

  const raw = form.account.trim()
  const local = raw.split('@', 2)[0]
  validateUsername(local)

  const finalName = `${local}@${teamDomain}`
  const accountUserName = local

  let created: CreateTeamMemberRes
  try {
    const resp = await API.createTeamMember({
      res_id: teamResID,
      name: finalName,
      role: form.role as TeamRole,
    } as any)

    created = assertOk(resp, errMap)
  } catch (e: any) {
    if (e?.__business) {
      if (!e.__shown) {
        throw e
      }
    }

    const handled = e?.isAxiosError ? handleResponseError(e, false) : handleRequestError(e, false)

    const key = String(handled?.code ?? 'default')
    const msg = errMap[key]?.msg || handled?.msg || errMap.default.msg

    const err = new Error(msg)
    ;(err as any).code = handled?.code ?? key
    throw err
  }

  const memberResID = created?.member?.resID
  if (!memberResID) throw new Error('Create member failed: missing resID in response')

  const serverPassword = created?.member?.password

  if (form.password?.trim()) {
    try {
      await API.updateTeamMemberPassword({
        res_id: memberResID,
        password: form.password.trim(),
      } as any)
    } catch (e) {
      console.warn('update password failed (non-blocking)', e)
    }
  }

  try {
    if (form.alias?.trim()) {
      await API.updateTeamMemberNickName({
        res_id: memberResID,
        nickname: form.alias.trim(),
      } as any)
    }
    if (form.note?.trim()) {
      await API.updateTeamMemberNote({ res_id: memberResID, note: form.note.trim() } as any)
    }
  } catch (e) {
    console.warn('update nickname/note failed (non-blocking)', e)
  }

  let memberRow = null
  try {
    const detail = await API.getTeamMember({ res_id: memberResID } as any)
    memberRow = normalizeMember({ res_id: memberResID, resID: memberResID, ...detail })
  } catch (e) {
    console.warn('get member detail failed (non-blocking)', e)
  }

  const finalPassword = form.password?.trim() || serverPassword || ''

  const creditsToAssign = Number.isFinite(form.assignCredit) ? Number(form.assignCredit) : 0
  if (creditsToAssign > 0) {
    try {
      await assignCredits(teamResID, [String(memberResID)], creditsToAssign)
    } catch (e) {
      console.warn('assign credits failed (non-blocking)', e)
    }
  }

  return {
    resultForDialog: {
      id: String(memberResID),
      account: finalName,
      accountUserName,
      role: form.role,
      credits: creditsToAssign,
      password: finalPassword,
    },
    memberRow,
  }
}

export async function assignCredits(
  teamResID: string,
  memberIds: string[],
  perMemberAmount: number,
) {
  if (!teamResID) throw new Error('Missing team resID')
  if (!memberIds?.length) throw new Error('No members selected')
  if (!(perMemberAmount > 0)) throw new Error('Amount must be > 0')

  const amount_cent = Math.round(perMemberAmount * 100)
  try {
    const res = (await API.distributeCredit({
      res_id: teamResID,
      member_res_ids: memberIds,
      amount_cent,
    } as any)) as DistributeCreditRes & { code?: number; msg?: string }
    if (res?.code !== undefined && res.code !== 0) {
      throw new Error(res.msg || `Assign credits failed (code ${res.code})`)
    }
    return res
  } catch (e) {
    console.error('assignCredits failed:', e)
    throw e
  }
}

export async function reclaimCredits(teamResID: string, memberId: string, amount: number) {
  if (!teamResID) throw new Error('Missing team resID')
  if (!memberId) throw new Error('Missing member resID')
  if (!(amount > 0)) throw new Error('Amount must be > 0')

  const amount_cent = Math.round(amount * 100)
  try {
    const res = (await API.reClasimMemberCredits({
      res_id: teamResID,
      member_res_id: memberId,
      amount_cent,
    })) as { code?: number; msg?: string }
    if (res?.code !== undefined && res.code !== 0) {
      throw new Error(res.msg || `Reclaim credits failed (code ${res.code})`)
    }
    return res
  } catch (e) {
    console.error('reclaimCredits failed:', e)
    throw e
  }
}

export async function suspendMembers(memberResIDs: string[]) {
  if (!memberResIDs?.length) return { amountOfSuccess: 0, amountOfFailure: 0 }
  const results = await Promise.allSettled(
    memberResIDs.map((id) => API.frozenTeamMember({ res_id: id })),
  )
  const amountOfSuccess = results.filter((r) => r.status === 'fulfilled').length
  const amountOfFailure = results.length - amountOfSuccess
  return { amountOfSuccess, amountOfFailure }
}

export async function unSuspendMembers(memberResIDs: string[]) {
  if (!memberResIDs?.length) return { amountOfSuccess: 0, amountOfFailure: 0 }
  const results = await Promise.allSettled(
    memberResIDs.map((id) => API.unFrozenTeamMember({ res_id: id })),
  )
  const amountOfSuccess = results.filter((r) => r.status === 'fulfilled').length
  const amountOfFailure = results.length - amountOfSuccess
  return { amountOfSuccess, amountOfFailure }
}

export async function removeMember(_teamResID: string, memberResID: string) {
  const deleteErrMap = formatErrCode({
    default: { msg: 'Delete failed' },
    204: errorMsg[204],
    205: errorMsg[205],
    195: errorMsg[195],
    1: errorMsg[1],
  })

  try {
    const resp = await API.removeTeamMember({ res_id: memberResID } as any)
    assertOk(resp, deleteErrMap)

    return true
  } catch (e: any) {
    if (e?.__business) throw e
    const handled = e?.isAxiosError ? handleResponseError(e, false) : handleRequestError(e, false)

    const key = String(handled?.code ?? 'default')
    const msg = deleteErrMap[key]?.msg || handled?.msg || deleteErrMap.default.msg

    const err = new Error(msg)
    ;(err as any).code = handled?.code ?? key
    throw err
  }
}

export async function removeMembers(memberResIDs: string[]) {
  // multiple concurrent, return success/failure list,
  // to facilitate partial success processing on the page
  const results = await Promise.allSettled(
    memberResIDs.map((id) => API.removeTeamMember({ res_id: id })),
  )
  const amountOfSuccess: string[] = []
  const amountOfFailure: { id: string; reason?: any }[] = []
  results.forEach((r, i) => {
    const id = memberResIDs[i]
    if (r.status === 'fulfilled') amountOfSuccess.push(id)
    else amountOfFailure.push({ id, reason: r.reason })
  })
  return { amountOfSuccess, amountOfFailure }
}

export async function updateMemberNickname(memberResID: string, nickname: string) {
  try {
    await API.updateTeamMemberNickName({ res_id: memberResID, nickname: nickname } as any)
    message.success('Name updated')
  } catch (e) {
    message.error('Update nickname failed')
  }
}

export async function updateMemberNote(memberResID: string, note: string) {
  try {
    await API.updateTeamMemberNote({ res_id: memberResID, note: note } as any)
    message.success('Note updated')
  } catch (e) {
    message.error('Update note failed')
  }
}

export async function updateMemberRole(memberResID: string, role: TeamRole) {
  const errMap = formatErrCode({
    default: { msg: 'Update role failed' },
    203: errorMsg[203],
  })

  try {
    const resp = await API.updateTeamMemberRole({ res_id: memberResID, role } as any)
    assertOk(resp, errMap)
    message.success('Role updated')
  } catch (e: any) {
    if (e?.__business) {
      throw e
    }
    const handled = e?.isAxiosError ? handleResponseError(e, false) : handleRequestError(e, false)
    const key = String(handled?.code ?? 'default')
    const msg = errMap[key]?.msg || handled?.msg || errMap.default.msg
    const err = new Error(msg)
    ;(err as any).code = handled?.code ?? key
    throw err
  }
}

export async function updateMemberPassword(memberResID: string, password: string) {
  try {
    await API.updateTeamMemberPassword({ res_id: memberResID, password: password } as any)
    message.success('Password updated')
  } catch (e) {
    message.error('Update password failed')
  }
}

export async function bindTeamMember(account: string, password: string) {
  await API.bindTeamMember({ name: account, password: password } as any)
}

export async function unbindTeamMember(teamResID: string) {
  await API.unbindTeamMember({ res_id: teamResID } as any)
}
